import { useEffect, useState, useMemo } from "react";
import { collection, doc, updateDoc, writeBatch, serverTimestamp } from "firebase/firestore";
import Swal from "sweetalert2";
import { db } from "../../firebase";
import { THEME_COLOR } from "../../config/club";
import Modal from "react-bootstrap/Modal";
export default function SessionPayments({ clubId, sessionId, admin, playerList, showPaymentModal, onClose, gamesPlayed }) {
  const themeAlpha = (opacity) => `${THEME_COLOR}${opacity}`;
  const [players, setPlayers] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]); // NEW: Multi-select state
  const [references, setReferences] = useState({});

  useEffect(() => {
    if (!clubId || !sessionId || !playerList) return;
    setPlayers(playerList);
  }, [clubId, sessionId, playerList]);

  const visiblePlayers = useMemo(() => {
    return players
      .filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase());
        const matchesFilter =
          filter === "all" ? true :
            filter === "paid" ? p.paid : !p.paid;
        return matchesSearch && matchesFilter;
      })
      .sort((a, b) => a.paid - b.paid);
  }, [players, search, filter]);

  // NEW: Toggle Selection Logic
  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // NEW: Bulk Payment Action using writeBatch
  const handleBulkPay = async (method) => {
    if (selectedIds.length === 0) return;

    const batch = writeBatch(db);

    selectedIds.forEach(id => {
      const ref = doc(db, "clubs", clubId, "sessions", sessionId, "players", id);
      batch.update(ref, {
        paid: true,
        paymentMethod: method,
        paymentReference: references[id] || null,
        paidAt: serverTimestamp(),
        markedBy: admin.uid
      });
    });

    try {
      await batch.commit();

      setReferences(prev => {
        const copy = { ...prev };
        selectedIds.forEach(id => delete copy[id]);
        return copy;
      });

      setSelectedIds([]);

      Swal.fire({
        icon: 'success',
        title: 'Payments Updated',
        timer: 1500,
        showConfirmButton: false
      });

    } catch (error) {
      console.error("Bulk update failed:", error);
    }
  };

  const markPaid = async (playerId, method) => {
    await updateDoc(
      doc(db, "clubs", clubId, "sessions", sessionId, "players", playerId),
      {
        paid: true,
        paymentMethod: method,
        paymentReference: references[playerId] || null,
        paidAt: serverTimestamp(),
        markedBy: admin.uid
      }
    );

    // clear reference input
    setReferences(prev => {
      const copy = { ...prev };
      delete copy[playerId];
      return copy;
    });
  };

  const markUnpaid = async (playerId) => {
    const confirm = await Swal.fire({
      title: "Reset Payment?",
      text: "This will mark the player as unpaid.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLOR,
      confirmButtonText: "Yes, reset"
    });

    if (!confirm.isConfirmed) return;

    await updateDoc(
      doc(db, "clubs", clubId, "sessions", sessionId, "players", playerId),
      {
        paid: false,
        paymentMethod: null,
        paidAt: null,
        markedBy: null
      }
    );
  };

  return (
    <Modal
      show={showPaymentModal}
      onHide={onClose}
      size="xl"
      centered
      scrollable
      contentClassName="border-0 shadow-lg rounded-5 overflow-hidden"
      style={{ color: "var(--text-color)" }}
    >
      {/* HEADER */}
      <Modal.Header closeButton className="border-0 px-4 pt-4 pb-2">
        <Modal.Title className="fw-bold d-flex align-items-center" style={{ color: THEME_COLOR }}>
          <div
            className="rounded-3 d-flex align-items-center justify-content-center me-3"
            style={{ width: 40, height: 40, backgroundColor: themeAlpha('10') }}
          >
            <i
              className="bi bi-credit-card-2-back"
              style={{ color: THEME_COLOR }}
            />
          </div>

          Payment Tracking
        </Modal.Title>
      </Modal.Header>

      {/* BODY */}
      <Modal.Body className="p-0 position-relative">

        {/* FLOATING BULK ACTION BAR */}
        {selectedIds.length > 0 && (
          <div
            className="position-sticky top-0 w-100 d-flex align-items-center justify-content-between px-3 py-2 shadow-lg"
            style={{
              zIndex: 10,
              backgroundColor: THEME_COLOR,
              color: "white",
              borderBottom: "1px solid var(--border-color)"
            }}
          >
            <div className="small fw-bold">
              {selectedIds.length} Selected
            </div>
            <div className="d-flex gap-2 align-items-center">
              <button
                className="btn btn-sm btn-light fw-bold rounded-pill shadow-sm"
                style={{ fontSize: "0.65rem" }}
                onClick={() => handleBulkPay("cash")}
              >
                💵 CASH
              </button>
              <button
                className="btn btn-sm btn-light fw-bold rounded-pill shadow-sm"
                style={{ fontSize: "0.65rem" }}
                onClick={() => handleBulkPay("gcash")}
              >
                📱 GCASH
              </button>
              <button
                className="btn btn-sm btn-link text-white p-0 ms-2"
                onClick={() => setSelectedIds([])}
              >
                <i className="bi bi-x-circle-fill"></i>
              </button>
            </div>
          </div>
        )}

        {/* FILTER / SEARCH */}
        <div
  className={`p-3 ${selectedIds.length > 0 ? "mt-5" : ""}`}
  style={{
    background: "var(--surface-bg)",
    borderBottom: "1px solid var(--border-color)"
  }}
>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span
  className="badge rounded-pill shadow-sm small"
  style={{
    background: "var(--card-bg)",
    color: "var(--text-color)",
    border: "1px solid var(--border-color)"
  }}
>
              {players.filter((p) => p.paid).length} / {players.length} Paid
            </span>
          </div>

          <div className="row g-2">
            <div className="col-12">
              <div className="input-group input-group-sm">
                <span
  className="input-group-text border-end-0"
  style={{
    background: "var(--card-bg)",
    color: "var(--text-muted)"
  }}
>
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0 shadow-none"
                  placeholder="Search player name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="col-12 d-flex gap-1">
              {["all", "unpaid", "paid"].map((f) => (
                <button
                  key={f}
                  onClick={() => {
                    setFilter(f);
                    setSelectedIds([]);
                  }}
                  className="btn btn-sm rounded-pill flex-grow-1 border-0 fw-bold text-uppercase"
                  style={{
                    fontSize: "0.65rem",
                    backgroundColor:
                      filter === f ? "#F5F3FF" : "transparent",
                    color:
                      filter === f ? THEME_COLOR : "#9CA3AF",
                  }}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* PLAYER LIST */}
        <div
          className="card-body p-0"
          style={{ maxHeight: "480px", overflowY: "auto" }}
        >
          {visiblePlayers.length === 0 ? (
            <div className="p-5 text-center text-muted">
              <i className="bi bi-person-x fs-2 opacity-25"></i>
              <p className="small mt-2">
                No players matching filters
              </p>
            </div>
          ) : (
            <div className="list-group list-group-flush">
              {visiblePlayers.map((player) => (
                <div
                  key={player.id}
                  className={`list-group-item py-3 border-0 border-bottom mx-2 px-2 d-flex align-items-center gap-3 ${selectedIds.includes(player.id)
                    ? "bg-light bg-opacity-50"
                    : ""
                    }`}
                  style={{
                    cursor: player.paid ? "default" : "pointer",
                  }}
                  onClick={() =>
                    !player.paid && toggleSelect(player.id)
                  }
                >
                  {/* CHECKBOX */}
                  {!player.paid && (
                    <div
                      className="rounded-circle border d-flex align-items-center justify-content-center"
                      style={{
                        width: 20,
                        height: 20,
                        minWidth: 20,
                        backgroundColor: selectedIds.includes(
                          player.id
                        )
                          ? THEME_COLOR
                          : "white",
                        borderColor: selectedIds.includes(
                          player.id
                        )
                          ? THEME_COLOR
                          : "var(--border-color)",
                      }}
                    >
                      {selectedIds.includes(player.id) && (
                        <i
                          className="bi bi-check text-white"
                          style={{ fontSize: "0.8rem" }}
                        />
                      )}
                    </div>
                  )}

                  {/* PLAYER INFO */}
                  <div className="d-flex align-items-center justify-content-between flex-grow-1">
                    <div className="d-flex align-items-center gap-2">
                      <img
                        src={player.photoURL || "/img/rrc2.png"}
                        className="rounded-circle border"
                        style={{
                          width: 42,
                          height: 42,
                          objectFit: "cover",
                        }}
                        alt=""
                      />
                      <div>
                        <div className="fw-bold" style={{ fontSize: "0.9rem" }}>
                          {player.name}
                        </div>
                        <div
                          className="text-muted"
                          style={{ fontSize: "0.75rem" }}
                        >
                          Lvl {player.level}
                        </div>
                      </div>
                      
                    </div>
                        <div
                        className="d-flex align-items-center text-muted"
                        style={{ fontSize: "0.75rem" }}
                      >
                        Number of Games Played ({gamesPlayed[player.name] || 0})
                      </div>

                    {/* PAYMENT ACTION */}
                    <div
                      className="text-end"
                      onClick={(e) => e.stopPropagation()}
                    >
                      
                      {player.paid ? (
                        <div className="d-flex flex-column align-items-end">
                          <span
                            className="badge rounded-pill d-flex align-items-center gap-1 shadow-sm"
                            style={{
                              backgroundColor: themeAlpha("15"),
color: THEME_COLOR,
                              fontSize: "0.75rem",
                              padding: "5px 10px"
                            }}
                          >
                            <i className="bi bi-check-circle-fill"></i>
                            {player.paymentMethod?.toUpperCase()}
                            {player.paymentReference && ` • ${player.paymentReference}`}
                          </span>
                          <button
                            className="btn btn-link btn-sm text-danger text-decoration-none p-0 mt-1 fw-bold"
                            style={{ fontSize: "0.65rem" }}
                            onClick={() =>
                              markUnpaid(player.id)
                            }
                          >
                            UNDO
                          </button>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center gap-2">

                          <input
                            type="text"
                            placeholder="Ref / Note"
                            className="form-control form-control-sm"
                            style={{ width: "150px", fontSize: "0.75rem", height: "32px" }}
                            value={references[player.id] || ""}
                            onChange={(e) =>
                              setReferences(prev => ({
                                ...prev,
                                [player.id]: e.target.value
                              }))
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div
                            className="btn-group shadow-sm rounded-pill overflow-hidden"
                            style={{
                              border: "1px solid var(--border-color)",
                            }}
                          >
                            <button
                              className="btn btn-sm btn-white fw-bold border-0 px-2"
                              style={{ fontSize: "0.75rem", height: "32px" }}
                              onClick={() =>
                                markPaid(player.id, "cash")
                              }
                            >
                              💵 CASH
                            </button>
                            <button
                              className="btn btn-sm btn-white fw-bold border-0 border-start px-2"
                              style={{
                                fontSize: "0.75rem",
                                height: "32px",
                                color: "#007bff",
                              }}
                              onClick={() =>
                                markPaid(player.id, "gcash")
                              }
                            >
                              📱 GCASH
                            </button>
                          </div>


                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* FOOTER ACCENT */}
        <div
          style={{
            height: 4,
            background: THEME_COLOR,
            opacity: 0.1,
          }}
        />
      </Modal.Body>
    </Modal>
  );
}